import { Check, X, Circle, Sparkles } from "lucide-react";

/**
 * Feature Matrix — head-to-head capability comparison against the leading
 * personal-finance apps. This is a static, in-product marketing/roadmap
 * surface (no backend). It grounds the "Life OS" positioning in concrete
 * feature gaps rather than abstract vibes.
 *
 * Legend:
 *   Check     — fully supported
 *   Circle    — partially / with workarounds
 *   X         — not offered
 *   Sparkles  — Life OS unique (or first-class where competitors add-on)
 */

const APPS = [
    { key: "monarch", label: "Monarch", tagline: "All-in-one PFM" },
    { key: "copilot", label: "Copilot", tagline: "Apple-focused" },
    { key: "ynab", label: "YNAB", tagline: "Budgeting" },
    { key: "rocket", label: "Rocket", tagline: "Cancel & negotiate" },
    { key: "empower", label: "Empower", tagline: "Net worth" },
    { key: "origin", label: "Origin", tagline: "AI advice" },
    { key: "nexus", label: "VAULT", tagline: "Yours", highlight: true },
];

const CAPS = [
    { section: "Money" },
    ["Credit card management (40+ cards)", { monarch: "P", copilot: "P", ynab: "P", rocket: "P", empower: "N", origin: "P", nexus: "Y*" }],
    ["Monthly statement uploads + parsing", { monarch: "P", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Payment tracking + posting reconciliation", { monarch: "P", copilot: "P", ynab: "P", rocket: "N", empower: "N", origin: "N", nexus: "Y" }],
    ["Forex card management (multi-currency)", { monarch: "N", copilot: "P", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Recurring subscription detection", { monarch: "Y", copilot: "Y", ynab: "P", rocket: "Y", empower: "P", origin: "Y", nexus: "Y" }],
    ["Subscription cancellation assistance", { monarch: "P", copilot: "N", ynab: "N", rocket: "Y", empower: "N", origin: "P", nexus: "P" }],
    ["Budget vs. spend by category", { monarch: "Y", copilot: "Y", ynab: "Y", rocket: "P", empower: "P", origin: "Y", nexus: "P" }],
    ["Cash flow forecasting", { monarch: "Y", copilot: "P", ynab: "Y", rocket: "N", empower: "Y", origin: "Y", nexus: "P" }],
    ["Investment / net-worth tracking", { monarch: "Y", copilot: "Y", ynab: "N", rocket: "P", empower: "Y", origin: "Y", nexus: "P" }],
    ["Credit score tracking (multi-bureau)", { monarch: "P", copilot: "N", ynab: "N", rocket: "Y", empower: "P", origin: "P", nexus: "Y*" }],

    { section: "Beyond money" },
    ["Asset registry (electronics, watches, jewellery)", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Warranty / AppleCare / insurance expiry tracker", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Home construction / interiors project hub", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Vehicle hub (insurance / PUC / FASTag / RC)", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Life Timeline (searchable financial memory)", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Passport / Visa / DL / AMC / Domain renewals", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Rewards milestone nudges (\"₹X away from voucher\")", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Best-card recommender per merchant", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "P", nexus: "Y*" }],
    ["Financial Health Score with actionable nudges", { monarch: "P", copilot: "N", ynab: "N", rocket: "P", empower: "P", origin: "Y", nexus: "Y" }],

    { section: "Security & control" },
    ["Step-up 2FA for sensitive actions", { monarch: "P", copilot: "P", ynab: "P", rocket: "P", empower: "P", origin: "P", nexus: "Y*" }],
    ["Encrypted statement PDF passwords", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Audit trail on every download", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Role-based access (admin / finance / viewer)", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Universal Print / Save-as-PDF", { monarch: "P", copilot: "P", ynab: "P", rocket: "P", empower: "P", origin: "P", nexus: "Y" }],

    { section: "Automation" },
    ["Gmail transaction & statement sync", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["CRED payment confirmation parser", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["Credit score email parser", { monarch: "N", copilot: "N", ynab: "N", rocket: "N", empower: "N", origin: "N", nexus: "Y*" }],
    ["AI copilot Q&A over your finances", { monarch: "P", copilot: "P", ynab: "N", rocket: "P", empower: "N", origin: "Y", nexus: "Y" }],
];

function Cell({ value }) {
    if (value === "Y*") return <span className="inline-flex items-center gap-1 text-emerald-700"><Sparkles size={13} /><span className="text-[11px] font-medium">Vault</span></span>;
    if (value === "Y") return <Check size={14} className="text-emerald-600 mx-auto" />;
    if (value === "P") return <Circle size={11} className="text-amber-500 fill-amber-100 mx-auto" strokeWidth={2} />;
    if (value === "N") return <X size={13} className="text-stone-300 mx-auto" />;
    return null;
}

export default function Features() {
    return (
        <div data-testid="features-page">
            <div className="mb-6">
                <div className="eyebrow flex items-center gap-1.5"><Sparkles size={12} /> VAULT · Feature Matrix</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2 max-w-3xl">
                    Everything the best finance apps do &mdash; and everything they don&apos;t.
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    A candid head-to-head against the six most-loved personal finance apps.
                    VAULT is the only product treating your <em>assets, home, vehicles, warranties,
                    documents and financial memory</em> as first-class citizens alongside money &mdash;
                    which is where daily life actually happens.
                </p>
            </div>

            <div className="flex items-center gap-4 text-[11px] text-stone-500 mb-4 flex-wrap">
                <span className="inline-flex items-center gap-1"><Check size={12} className="text-emerald-600" /> Supported</span>
                <span className="inline-flex items-center gap-1"><Circle size={9} className="text-amber-500 fill-amber-100" strokeWidth={2} /> Partial / workaround</span>
                <span className="inline-flex items-center gap-1"><X size={12} className="text-stone-300" /> Not offered</span>
                <span className="inline-flex items-center gap-1"><Sparkles size={11} className="text-emerald-700" /> First-class in Vault</span>
            </div>

            <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-stone-50 border-b border-stone-200">
                                <th className="text-left px-4 py-3 font-medium text-stone-700 sticky left-0 bg-stone-50 z-10">Capability</th>
                                {APPS.map((a) => (
                                    <th key={a.key} className={`px-2 py-3 text-center align-bottom ${a.highlight ? "bg-emerald-50" : ""}`}>
                                        <div className={`text-[13px] font-medium ${a.highlight ? "text-emerald-800" : "text-stone-700"}`}>{a.label}</div>
                                        <div className="text-[10px] text-stone-500 mt-0.5 font-normal normal-case tracking-normal">{a.tagline}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {CAPS.map((row, i) => {
                                if (typeof row === "object" && !Array.isArray(row)) {
                                    return (
                                        <tr key={`s-${i}`} className="bg-stone-100/50">
                                            <td colSpan={APPS.length + 1} className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-stone-500 font-medium">
                                                {row.section}
                                            </td>
                                        </tr>
                                    );
                                }
                                const [label, cells] = row;
                                return (
                                    <tr key={i} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/50 transition-colors">
                                        <td className="px-4 py-2.5 text-stone-800 text-[13px] sticky left-0 bg-white hover:bg-stone-50/50 z-10">{label}</td>
                                        {APPS.map((a) => (
                                            <td key={a.key} className={`px-2 py-2.5 text-center ${a.highlight ? "bg-emerald-50/40" : ""}`}>
                                                <Cell value={cells[a.key]} />
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="mt-6 text-[11px] text-stone-500 leading-relaxed max-w-3xl">
                Assessment based on public product documentation and reviews as of early 2026.
                &ldquo;Partial&rdquo; typically means the capability is possible via a workaround, plug-in or manual
                effort but not first-class. VAULT is designed as the connective tissue between all these
                slices &mdash; a single place where every card, expense, asset, document, renewal, subscription
                and life event lives together.
            </div>
        </div>
    );
}
